import {
  AllSitesAlarmType,
  SiteAlarmType,
} from '../../features/alarms/alarmsAPI';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import convertIsoToDateTimeString from '../../utils/time';
import buildGraphingUrl from './utils/buildGraphingUrl';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value: string | boolean;
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Typography variant="body1" sx={{ mt: 0.5 }}>
      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
    </Typography>
  </Box>
);

type SiteAlarmProps = {
  open: boolean;
  onClose: () => void;
  alarm: SiteAlarmType;
  leoAlertAvailable: boolean;
  siteId: number;
};

type AllSitesAlarmProps = {
  open: boolean;
  onClose: () => void;
  alarm: AllSitesAlarmType;
  leoAlertAvailable?: boolean;
  siteId?: never;
};

type Props = SiteAlarmProps | AllSitesAlarmProps;

const AlarmInfoDialog = ({
  open,
  onClose,
  alarm,
  siteId,
  leoAlertAvailable = false,
}: Props) => {
  const navigate = useNavigate();

  const graphingAvailable =
    alarm.application !== null &&
    alarm.application !== '0' &&
    alarm.alarm_source;

  const handleGraph = () => {
    if (!graphingAvailable) return;

    const url = buildGraphingUrl(alarm.alarm_source, alarm);

    const startDate = DateTime.fromISO(alarm.alarm_date)
      .minus({ hours: 1 })
      .toUTC()
      .toISO();
    const endDate = DateTime.now().toUTC().toISO();

    navigate(url, {
      state: {
        graphRequestDates: {
          startDate,
          endDate,
        },
        from: location.pathname,
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Alarm Info</DialogTitle>
      <DialogContent dividers>
        {/* Need to display alarm info in this order
          1. alarm_date (Date/Time)
          2. alarm_text (Description)
          3. siteName (Site Name) only if siteId is undefined
          4. controller_name (Controller)
          5. alarm_source (Source)
          6. leoAlertSent (LEO Alert Sent) if available
         */}
        <InfoRow
          label="Date/Time"
          value={convertIsoToDateTimeString(alarm.alarm_date, {
            zone: alarm.timezone,
          })}
        />
        <InfoRow label="Description" value={alarm.alarm_text} />

        {siteId === undefined && (
          <InfoRow label="Site Name" value={alarm.siteName} />
        )}

        <InfoRow label="Controller" value={alarm.controller_name} />
        <InfoRow label="Source" value={alarm.alarm_source} />

        {alarm.service_override_date !== null && (
          <InfoRow
            label="Override Until"
            value={convertIsoToDateTimeString(alarm.service_override_date, {
              zone: alarm.timezone,
            })}
          />
        )}

        {alarm.rtn_date && (
          <InfoRow
            label="RTN Date"
            value={convertIsoToDateTimeString(alarm.rtn_date, {
              zone: alarm.timezone,
            })}
          />
        )}

        {leoAlertAvailable && (
          <InfoRow
            label="LEO Alert Sent"
            value={alarm.leoAlertStatus ?? 'N/A'}
          />
        )}
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        {graphingAvailable && <Button onClick={handleGraph}>Graph</Button>}
        <Button sx={{ marginLeft: 'auto' }} color="secondary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlarmInfoDialog;
